<template>
  <wb-accordion-item
    class="personal-data-selection-box"
    :class="[
      getSelectionBoxClass('personal-data-selection-box'),
      'request-flow-selector',
    ]"
    :open="boxIsOpen"
    ref="accordionRef"
  >
    <wb-accordion-toggle
      class="personal-data-selection-box__toggle"
      @click="toggle"
    >
      <div class="personal-data-selection-box__step">
        <span v-if="!showSuccessIcon">{{ step }}</span>
        <wb-icon
          v-else
          name="bds/checkmark/16"
          class="personal-data-selection-box__step--success"
        ></wb-icon>
      </div>
      <div
        class="personal-data-selection-box__label"
        :class="personalDataSelectionBoxLabelFont"
      >
        <wb-icon name="bds/pencil-document/24"></wb-icon>
        <span v-if="showToggleLabel">{{
          $t('schedule.request.selectionBoxes.personalData')
        }}</span>
      </div>
      <div class="personal-data-selection-box__selected-value">
        <span v-if="showSuccessIcon" class="wb-type-copy">
          {{ selectedValue }}
        </span>
      </div>
    </wb-accordion-toggle>
    <wb-accordion-section class="mobile-overflow-wb-disable">
      <wb-accordion-mobile-header @click="closeAccordion(emit)">{{
        $t('schedule.filter.back')
      }}</wb-accordion-mobile-header>
      <wb-accordion-content class="personal-data-selection-box__form">
        <div
          class="personal-data-selection-box__label__inModal"
          :class="retailerSelectionBoxLabelFont"
          v-if="isMobile || isMq3"
        >
          <wb-icon
            class="personal-data-selection-box__label__inModal-icon"
            id="personal-data-selection-box__label__inModal-icon"
            name="bds/location-dealer/24"
          ></wb-icon>
          <span
            class="personal-data-selection-box__label__inModal-text wb-type-heading-s"
            >{{ $t('schedule.request.selectionBoxes.personalData') }}</span
          >
        </div>
        <widget-loader :resources="aemFormsResources" />
        <aemf-form-container
          formname="OTB_PERSONAL_DATA_FORM"
          @aemf-form-loaded="onAemFormLoad"
          @aemf-form-submit-success="onAemFormSubmit"
          :appid="appId"
          :formurl="aemRequestFormLink"
          :formInitialData="formInitialData"
          :key="formRenderKey"
          destroyonunmount
        />
        <!-- TODO: On form fail pass the error with @aemf-form-submit-fail="onAemFormFail" -->
      </wb-accordion-content>
    </wb-accordion-section>
  </wb-accordion-item>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue';

import { WidgetLoader } from '../../index';

import useAemForm from '../../../composables/aem-form.composable';
import useRequestSelectionBoxes from '../../../composables/request-selection-boxes.composable';
import { useStore } from '../../../composables/store.composable';
import { useProspect } from '../../../composables/prospect.composable';
import { useMediaQuery } from '../../../composables/media-query.composable';

import {
  RequestSelectionBoxesStatus,
  RequestSelectionBoxesStatusType,
  RequestSelectionBoxesStep,
} from '../../../models/entities/request-selection-boxes.model';

import UtilsService from '../../../services/utils.service';
import { i18n } from '../../../services/translation.service';

export default defineComponent({
  i18n,
  name: 'otb-personal-data-selection-box',
  components: {
    'widget-loader': WidgetLoader,
  },
  props: {
    status: {
      type: String as PropType<RequestSelectionBoxesStatusType>,
      default: RequestSelectionBoxesStatus.TODO,
    },
    step: {
      type: Number,
      default: 1,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    currentStep: String,
    accordionClosingHandler: {
      type: Function,
      default: () => () => {
        return;
      },
    },
  },
  setup(props, { emit }) {
    const { state } = useStore();
    const { isMq3 } = useMediaQuery();

    const { buildProspectName } = useProspect();

    const { closeAccordion, selectionBoxIsDone, selectionBoxClass } =
      useRequestSelectionBoxes();

    const accordionRef = ref();

    const {
      appId,
      formInitialData,
      aemRequestFormLink,
      aemFormsResources,
      formRenderKey,
      renderForm,
      storeProspectBeforeSendData,
    } = useAemForm();

    const showSuccessIcon = computed(() => {
      return selectionBoxIsDone(props.status);
    });

    const getSelectionBoxClass = (mainClass: string) => {
      return selectionBoxClass(props.status, mainClass);
    };

    const boxIsOpen = computed(() => {
      const isBoxOpen =
        props.currentStep == RequestSelectionBoxesStep.PERSONAL_DATA;

      if (isBoxOpen) emit('onBoxOpen', RequestSelectionBoxesStep.PERSONAL_DATA);
      return isBoxOpen;
    });

    const onAemFormLoad = (): void => {
      emit('onToggle');
    };

    const onAemFormSubmit = async (event: any): Promise<void> => {
      props.accordionClosingHandler();

      emit('changeStatus');
      await storeProspectBeforeSendData(event.detail[0].formValues);
      toggle();
    };

    const selectedValue = computed(() => {
      const { customerContactsSet, customerNameSet } = state.otbcFlow.prospect;

      const hasCustomerName =
        customerNameSet && Object.keys(customerNameSet).length > 0;
      const hasCustomerContacts =
        customerContactsSet && Object.keys(customerContactsSet).length > 0;

      return hasCustomerName && hasCustomerContacts
        ? `${buildProspectName(customerNameSet)}, ${
            customerContactsSet.email
          }, ${customerContactsSet.phone || customerContactsSet.mobilePhone}`
        : '';
    });

    const personalDataSelectionBoxLabelFont = computed(() => {
      return props.isMobile ? 'wb-type-copy-strong' : 'wb-type-heading-s';
    });

    const toggle = () => {
      emit('onToggle');
      const isOpen = accordionRef.value.hasAttribute('open');
      renderForm();
      if (!isOpen) {
        props.accordionClosingHandler();
        UtilsService.closeAllAccordions();
        emit('open', 'personalData');
      }
    };

    const showToggleLabel = computed(() => {
      return (
        props.status === RequestSelectionBoxesStatus.AVAILABLE ||
        props.status === RequestSelectionBoxesStatus.TODO
      );
    });

    return {
      appId,
      aemFormsResources,
      boxIsOpen,
      formInitialData,
      aemRequestFormLink,
      showSuccessIcon,
      showToggleLabel,
      selectedValue,
      accordionRef,
      formRenderKey,
      closeAccordion,
      emit,
      getSelectionBoxClass,
      onAemFormLoad,
      onAemFormSubmit,
      toggle,
      personalDataSelectionBoxLabelFont,
      isMq3,
    };
  },
});
</script>

<style scoped lang="scss" src="./personal-data-selection-box.scss"></style>
